import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/api/axiosClient';
import type { ProcessConfigurationDto } from '@/types/configuration';

/** Shared GET /process-configuration fetch — single query key so every caller shares one cached
 * copy instead of each page firing its own request. Used both for in-page conditional workflow
 * logic (e.g. GRN label print timing) and for gating nav visibility in AppShell.
 *
 * Pass `companyId` for a SuperAdmin session viewing one specific company (via the company/{id}
 * routes) instead of "the current user's own company" — the self-service route 400s for a
 * company-less SuperAdmin, which without this would silently make every config-gated nav item
 * look "enabled" regardless of what the company being viewed actually has set. Leave it
 * `undefined` (the default) for the normal case; pass `null` explicitly to skip fetching
 * altogether (SuperAdmin browsing somewhere with no company in view). */
export function useProcessConfiguration(companyId?: string | null) {
  const useCompanyRoute = companyId !== undefined;

  return useQuery({
    queryKey: useCompanyRoute ? ['process-configuration', 'company', companyId] : ['process-configuration'],
    queryFn: async () =>
      (
        await apiClient.get<ProcessConfigurationDto>(
          useCompanyRoute ? `/process-configuration/company/${companyId}` : '/process-configuration',
        )
      ).data,
    enabled: useCompanyRoute ? Boolean(companyId) : true,
    // AppShell's nav consumes this and lives for the whole authenticated session without
    // remounting, so on the app-wide default (refetchOnWindowFocus: false, no interval) it would
    // otherwise only ever see the value from the moment the tab first loaded — missing a change
    // saved from another session (e.g. a SuperAdmin editing this same company elsewhere) until the
    // page is hard-refreshed. Overridden here so nav gating stays reasonably live.
    refetchOnWindowFocus: true,
    refetchInterval: 60_000,
  });
}
